import React, { useState } from "react";
import {
  UserIcon,
  ShieldCheckIcon,
  KeyIcon,
  PencilIcon,
  IdentificationIcon,
  CalendarIcon,
  LockClosedIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../hooks";
import { useProfile } from "../features/profile/hooks";
import { ChangePasswordModal } from "../components/profile";
import ErrorDisplay from "../components/common/ErrorDisplay";

/**
 * Profile page component showing current user's details
 *
 * Features:
 * - Display complete user information from auth.me endpoint
 * - Show user role and status with tooltips
 * - Professional card-based layout with 2x2 grid
 * - Responsive design
 */

const ProfilePage = () => {
  const { user, isLoading } = useAuth();
  const {
    updateProfile,
    isLoading: isUpdatingProfile,
    error: profileError,
  } = useProfile();

  // Modal states
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Edit mode states
  const [isEditingPersonal, setIsEditingPersonal] = useState(false);
  const [isEditingSecurity, setIsEditingSecurity] = useState(false);

  // Form states
  const [personalForm, setPersonalForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [securityForm, setSecurityForm] = useState({
    mfaEnabled: false,
  });

  // Validation states
  const [emailError, setEmailError] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  // Initialize form data when user data loads
  React.useEffect(() => {
    if (user) {
      setPersonalForm({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
      });
      setSecurityForm({
        mfaEnabled: user.mfaEnabled || false,
      });
    }
  }, [user]);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      return "Email is required";
    }
    if (!emailRegex.test(email)) {
      return "Please enter a valid email address";
    }
    return "";
  };

  const handleEmailBlur = () => {
    const error = validateEmail(personalForm.email);
    setEmailError(error);
  };

  const handlePersonalEdit = () => {
    setIsEditingPersonal(true);
    setEmailError("");
  };

  const handlePersonalCancel = () => {
    setIsEditingPersonal(false);
    setPersonalForm({
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      email: user?.email || "",
    });
    setEmailError("");
  };

  const handlePersonalSave = async () => {
    const emailValidationError = validateEmail(personalForm.email);
    if (emailValidationError) {
      setEmailError(emailValidationError);
      return;
    }

    const result = await updateProfile({
      firstName: personalForm.firstName,
      lastName: personalForm.lastName,
      email: personalForm.email,
    });

    if (result.success) {
      setIsEditingPersonal(false);
      setSuccessMessage("Personal information updated successfully!");
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(""), 3000);
    }
  };

  const handleSecurityEdit = () => {
    setIsEditingSecurity(true);
  };

  const handleSecurityCancel = () => {
    setIsEditingSecurity(false);
    setSecurityForm({
      mfaEnabled: user?.mfaEnabled || false,
    });
  };

  const handleSecuritySave = async () => {
    // For now, we'll implement MFA settings update
    // This might need to be expanded based on available API endpoints
    const result = await updateProfile({
      // Note: This might need adjustment based on actual API schema
      mfaEnabled: securityForm.mfaEnabled,
    } as any);

    if (result.success) {
      setIsEditingSecurity(false);
      setSuccessMessage("Security settings updated successfully!");
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(""), 3000);
    }
  };

  const handleCancelPendingEmail = async () => {
    // This would need to be implemented with a specific API endpoint
    // For now, we'll use a placeholder
    setIsUpdating(true);
    try {
      // TODO: Implement actual API call to cancel pending email change
      console.log("Canceling pending email change");

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setSuccessMessage("Pending email change has been cancelled!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Failed to cancel pending email change:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const canEnableMFA = () => {
    return !user?.pendingEmail; // Can only enable MFA if no pending email change
  };

  const handleChangePassword = () => {
    setChangePasswordModalOpen(true);
  };

  const handlePasswordChangeSuccess = () => {
    setSuccessMessage("Password changed successfully!");
    // Clear success message after 3 seconds
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-500 dark:text-gray-400">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <p className="text-danger">Unable to load profile information</p>
        </div>
      </div>
    );
  }

  const getRoleStyles = (role: string) => {
    switch (role) {
      case "ADMIN":
        return "bg-danger/10 text-danger border-danger/20";
      case "MANAGER":
        return "bg-warning/10 text-warning border-warning/20";
      case "SUPERVISOR":
        return "bg-info/10 text-info border-info/20";
      case "SERVER":
        return "bg-success/10 text-success border-success/20";
      case "USER":
        return "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600";
    }
  };

  const getStatusStyles = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "bg-success/10 text-success border-success/20";
      case "UNVERIFIED":
        return "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-800";
      case "CREATED":
        return "bg-info/10 text-info border-info/20";
      case "SUSPENDED":
        return "bg-danger/10 text-danger border-danger/20";
      case "LOCKED":
        return "bg-danger/10 text-danger border-danger/20";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600";
    }
  };

  const getStatusTooltip = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "Account is active and fully functional";
      case "UNVERIFIED":
        return "Account email address has not been verified yet";
      case "CREATED":
        return "Account has been created but setup is not complete";
      case "SUSPENDED":
        return "Account has been temporarily suspended by an administrator";
      case "LOCKED":
        return "Account has been locked due to security concerns";
      default:
        return "Account status information";
    }
  };

  const getRoleTooltip = (role: string) => {
    switch (role) {
      case "ADMIN":
        return "Full system administration access with all permissions";
      case "MANAGER":
        return "Management capabilities with supervision and reporting access";
      case "SUPERVISOR":
        return "Team supervision and limited administrative functions";
      case "SERVER":
        return "Service role with customer interaction capabilities";
      case "USER":
        return "Standard user access with basic functionality";
      default:
        return "User role information";
    }
  };

  const getInitials = (
    firstName: string | undefined,
    lastName: string | undefined,
  ) => {
    if (!firstName && !lastName) return "U";

    const first = firstName?.charAt(0) || "";
    const last = lastName?.charAt(0) || "";
    return (first + last).toUpperCase() || "U";
  };

  const getFullName = () => {
    const firstName = user.firstName || "";
    const lastName = user.lastName || "";
    return `${firstName} ${lastName}`.trim() || "Unknown User";
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="font-serif text-3xl font-normal tracking-[-0.005em] text-[var(--text)]">
          My Profile
        </h1>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6">
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-4">
            <p className="text-green-800 dark:text-green-200">
              {successMessage}
            </p>
          </div>
        </div>
      )}

      {/* Profile Error Display */}
      {profileError && (
        <div className="mb-6">
          <ErrorDisplay error={profileError} />
        </div>
      )}

      <div className="card">
        {/* Header Section with Avatar */}
        <div className="flex items-center mb-6 pb-6 border-b border-gray-200 dark:border-darkmode-500">
          <div className="w-20 h-20 rounded-full bg-gradient-to-b from-theme-1 to-theme-2 flex items-center justify-center text-white text-2xl font-semibold mr-6">
            {getInitials(user.firstName, user.lastName)}
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-500 dark:text-white mb-2">
              {getFullName()}
            </h2>
            <div className="flex gap-2">
              <div className="relative group" style={{ cursor: "help" }}>
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getRoleStyles(user.role || "USER")}`}
                >
                  <ShieldCheckIcon className="w-3 h-3 mr-1" />
                  {user.role || "USER"}
                </span>
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-10">
                  {getRoleTooltip(user.role || "USER")}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                </div>
              </div>
              <div className="relative group" style={{ cursor: "help" }}>
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusStyles(user.accountStatus || "CREATED")}`}
                >
                  {user.accountStatus || "CREATED"}
                </span>
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-10">
                  {getStatusTooltip(user.accountStatus || "CREATED")}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                </div>
              </div>
            </div>
          </div>
          <button className="btn-primary" onClick={handleChangePassword}>
            <KeyIcon className="w-4 h-4 mr-2" />
            Change Password
          </button>
        </div>

        {/* Details Grid - 2x2 Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Personal Information */}
          <div className="bg-gray-50 dark:bg-darkmode-700 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <UserIcon className="w-5 h-5 text-primary mr-2" />
                <h3 className="text-lg font-medium text-gray-500 dark:text-white">
                  Personal Information
                </h3>
              </div>
              <div className="flex items-center gap-2">
                {!isEditingPersonal ? (
                  <button
                    onClick={handlePersonalEdit}
                    className="p-1.5 text-gray-500 hover:text-primary hover:bg-gray-200 dark:hover:bg-darkmode-600 rounded transition-colors"
                    title="Edit personal information"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handlePersonalCancel}
                      disabled={isUpdating}
                      className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                      title="Cancel changes"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={handlePersonalSave}
                      disabled={isUpdatingProfile || !!emailError}
                      className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors disabled:opacity-50"
                      title="Save changes"
                    >
                      {isUpdatingProfile ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                      ) : (
                        <CheckIcon className="w-4 h-4" />
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="space-y-3">
              {!isEditingPersonal ? (
                <>
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                      Name
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {getFullName()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                      Email Address
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {user.email || "Not provided"}
                    </p>
                    {user.pendingEmail && (
                      <div className="flex items-center justify-between mt-2 p-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded">
                        <p className="text-sm text-orange-600 dark:text-orange-400">
                          <ExclamationTriangleIcon className="w-4 h-4 inline mr-1" />
                          Awaiting Verification: {user.pendingEmail}
                        </p>
                        <button
                          onClick={handleCancelPendingEmail}
                          disabled={isUpdating}
                          className="text-xs text-orange-600 hover:text-orange-800 dark:text-orange-400 dark:hover:text-orange-300 underline disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
                        First Name
                      </label>
                      <input
                        type="text"
                        value={personalForm.firstName}
                        onChange={(e) =>
                          setPersonalForm((prev) => ({
                            ...prev,
                            firstName: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-darkmode-800 text-gray-500 dark:text-white"
                        placeholder="Enter first name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={personalForm.lastName}
                        onChange={(e) =>
                          setPersonalForm((prev) => ({
                            ...prev,
                            lastName: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-darkmode-800 text-gray-500 dark:text-white"
                        placeholder="Enter last name"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={personalForm.email}
                      onChange={(e) =>
                        setPersonalForm((prev) => ({
                          ...prev,
                          email: e.target.value,
                        }))
                      }
                      onBlur={handleEmailBlur}
                      className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-darkmode-800 text-gray-500 dark:text-white ${
                        emailError
                          ? "border-red-300 dark:border-red-600"
                          : "border-gray-300 dark:border-gray-600"
                      }`}
                      placeholder="Enter email address"
                    />
                    {emailError && (
                      <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                        {emailError}
                      </p>
                    )}
                    {user.pendingEmail && (
                      <div className="flex items-center justify-between mt-2 p-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded">
                        <p className="text-sm text-orange-600 dark:text-orange-400">
                          <ExclamationTriangleIcon className="w-4 h-4 inline mr-1" />
                          Awaiting Verification: {user.pendingEmail}
                        </p>
                        <button
                          onClick={handleCancelPendingEmail}
                          disabled={isUpdating}
                          className="text-xs text-orange-600 hover:text-orange-800 dark:text-orange-400 dark:hover:text-orange-300 underline disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Account Information */}
          <div className="bg-gray-50 dark:bg-darkmode-700 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <IdentificationIcon className="w-5 h-5 text-primary mr-2" />
              <h3 className="text-lg font-medium text-gray-500 dark:text-white">
                Account Information
              </h3>
            </div>

            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                  Role
                </p>
                <div
                  className="relative group inline-block"
                  style={{ cursor: "help" }}
                >
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleStyles(user.role || "USER")}`}
                  >
                    {user.role || "USER"}
                  </span>
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-10">
                    {getRoleTooltip(user.role || "USER")}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                  </div>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                  Account Status
                </p>
                <div
                  className="relative group inline-block"
                  style={{ cursor: "help" }}
                >
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusStyles(user.accountStatus || "CREATED")}`}
                  >
                    {user.accountStatus || "CREATED"}
                  </span>
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-10">
                    {getStatusTooltip(user.accountStatus || "CREATED")}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                  </div>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                  Account Active
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {user.isActive ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success/10 text-success border border-success/20">
                      Yes
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-danger/10 text-danger border border-danger/20">
                      No
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Security Information */}
          <div className="bg-gray-50 dark:bg-darkmode-700 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <ShieldCheckIcon className="w-5 h-5 text-primary mr-2" />
                <h3 className="text-lg font-medium text-gray-500 dark:text-white">
                  Security
                </h3>
              </div>
              <div className="flex items-center gap-2">
                {!isEditingSecurity ? (
                  <button
                    onClick={handleSecurityEdit}
                    className="p-1.5 text-gray-500 hover:text-primary hover:bg-gray-200 dark:hover:bg-darkmode-600 rounded transition-colors"
                    title="Edit security settings"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleSecurityCancel}
                      disabled={isUpdating}
                      className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                      title="Cancel changes"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={handleSecuritySave}
                      disabled={isUpdatingProfile}
                      className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors disabled:opacity-50"
                      title="Save changes"
                    >
                      {isUpdatingProfile ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                      ) : (
                        <CheckIcon className="w-4 h-4" />
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                  Multi-Factor Authentication
                </p>
                {!isEditingSecurity ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {user.mfaEnabled ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success/10 text-success border border-success/20">
                        Enabled
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700 border border-orange-200 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-800">
                        Disabled
                      </span>
                    )}
                  </p>
                ) : (
                  <div className="flex items-center space-x-3">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={securityForm.mfaEnabled}
                        onChange={(e) =>
                          setSecurityForm((prev) => ({
                            ...prev,
                            mfaEnabled: e.target.checked,
                          }))
                        }
                        disabled={!canEnableMFA() && !securityForm.mfaEnabled}
                        className="w-4 h-4 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary dark:focus:ring-primary dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 disabled:opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-500 dark:text-white">
                        Enable Multi-Factor Authentication
                      </span>
                    </label>
                    {!canEnableMFA() && !user.mfaEnabled && (
                      <p className="text-xs text-orange-600 dark:text-orange-400">
                        Please verify or cancel your pending email change first
                      </p>
                    )}
                  </div>
                )}
              </div>
              {user.mfaEnabled && user.mfaType && (
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                    MFA Type
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {user.mfaType}
                  </p>
                </div>
              )}
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                  Force Password Change
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {user.forcePasswordChange ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700 border border-orange-200 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-800">
                      <LockClosedIcon className="w-3 h-3 mr-1" />
                      Required
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success/10 text-success border border-success/20">
                      Not Required
                    </span>
                  )}
                </p>
              </div>
              {user.lastPasswordChange && (
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                    Last Password Change
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {new Date(user.lastPasswordChange).toLocaleDateString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      },
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Activity Information */}
          <div className="bg-gray-50 dark:bg-darkmode-700 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <CalendarIcon className="w-5 h-5 text-primary mr-2" />
              <h3 className="text-lg font-medium text-gray-500 dark:text-white">
                Activity
              </h3>
            </div>

            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                  Account Created
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {user.createdAt
                    ? new Date(user.createdAt).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : "Not available"}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                  Last Updated
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {user.updatedAt
                    ? new Date(user.updatedAt).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : "Not available"}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                  Last Login
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {user.lastLoginAt ? (
                    new Date(user.lastLoginAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  ) : (
                    <span className="text-gray-500 dark:text-gray-400">
                      Never logged in
                    </span>
                  )}
                </p>
              </div>
              {user.lastFailedLoginAttempt && (
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                    Last Failed Login
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {new Date(user.lastFailedLoginAttempt).toLocaleDateString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      },
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-darkmode-500">
          <p className="text-center text-sm text-gray-500 dark:text-gray-400">
            Profile information is automatically synchronized with the system.
            Contact your administrator if you need to update your details.
          </p>
        </div>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        open={changePasswordModalOpen}
        onClose={() => setChangePasswordModalOpen(false)}
        onSuccess={handlePasswordChangeSuccess}
      />
    </div>
  );
};

export default ProfilePage;
